import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, Signal } from '@angular/core';
import { OrIconComponent, PageActionsComponent, PageLayoutComponent, TitileNavbarComponent } from '@ui-components';
import {
  ActionModalComponent,
  AppRoutes,
  DateFormats,
  formatDateToString,
  GameDetailsModel,
  MODAL_TOKEN,
  ModalProvider,
  NAVIGATION_TOKEN,
  NavigationProvider,
  TeamsRoutes,
  TOAST_TOKEN,
  ToastProvider
} from '@core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { map, switchMap, take } from 'rxjs';
import { GetMyGamesQueryHandler, LoadMyGamesCommandHandler } from '@my-games';
import { MyGamesService, MyGamesStorage } from '@my-games/infra';
import { AssignGameItem, TeamInviteData } from '../../application/models';
import { AssignGamesToTeamCommandHandler, AssignGamesToTeamCommandResult } from '../../application/handlers';
import { AssignGamesToTeamComponent } from '../assign-games-to-team/assign-games-to-team.component';
import { ShareTeamModalComponent } from '../share-team-modal/share-team-modal.component';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'lib-create-team-finish',
  templateUrl: './create-team-finish.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [TitileNavbarComponent, PageLayoutComponent, PageActionsComponent, OrIconComponent],
  providers: [
    MyGamesService,
    MyGamesStorage,
    LoadMyGamesCommandHandler,
    GetMyGamesQueryHandler,
    AssignGamesToTeamCommandHandler
  ]
})
export class CreateTeamFinishComponent implements OnInit {
  private readonly navigationProvider: NavigationProvider = inject(NAVIGATION_TOKEN);
  private readonly router: Router = inject(Router);
  private readonly activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private readonly modalProvider: ModalProvider = inject(MODAL_TOKEN);
  private readonly toastProvider: ToastProvider = inject(TOAST_TOKEN);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly loadMyGamesCommandHandler: LoadMyGamesCommandHandler = inject(LoadMyGamesCommandHandler);
  private readonly getMyGamesQueryHandler: GetMyGamesQueryHandler = inject(GetMyGamesQueryHandler);
  private readonly assignGamesToTeamCommandHandler: AssignGamesToTeamCommandHandler = inject(
    AssignGamesToTeamCommandHandler
  );

  readonly teamData: Signal<TeamInviteData | undefined> = toSignal(
    this.activatedRoute.params.pipe(
      map((params: Params) => ({
        teamId: params['id'],
        token: params['token']
      }))
    )
  );

  ngOnInit(): void {
    this.proposeGameAssignment();
  }

  goToDetails(): void {
    const data: TeamInviteData | undefined = this.teamData();
    if (!data) return;

    this.navigationProvider.replace(`${AppRoutes.TEAMS}/${data.teamId}/${TeamsRoutes.TEAM_DETAILS}`);
  }

  invitePlayers(): void {
    const data: TeamInviteData | undefined = this.teamData();
    if (!data) return;

    this.navigationProvider.replaceHistoryEntry(`${AppRoutes.TEAMS}/${data.teamId}/${TeamsRoutes.TEAM_DETAILS}`);
    this.router.navigateByUrl(`${AppRoutes.TEAMS}/${data.teamId}/${data.token}/${TeamsRoutes.INVITE}`);
  }

  share(): void {
    const data: TeamInviteData | undefined = this.teamData();
    if (!data) return;

    this.modalProvider.showModal$({
      component: ShareTeamModalComponent,
      componentProps: { teamData: data },
      cssClass: 'modal-auto',
      initialBreakpoint: 1,
      breakpoints: [0, 1]
    });
  }

  private proposeGameAssignment(): void {
    this.loadMyGamesCommandHandler
      .load()
      .pipe(
        switchMap(() => this.getMyGamesQueryHandler.getFutureHosted$()),
        take(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((games: GameDetailsModel[]) => {
        if (!games.length) return;

        this.showAssignModal(games.map((game: GameDetailsModel) => this.toAssignItem(game)));
      });
  }

  private toAssignItem(game: GameDetailsModel): AssignGameItem {
    const date: string = formatDateToString(game.gameDateStart, DateFormats.DayMonthDot);
    const time: string = formatDateToString(game.gameDateStart, DateFormats.Time);

    return { gameId: game.gameId, label: `${game.gameName} (${date}, ${time})` };
  }

  private showAssignModal(games: AssignGameItem[]): void {
    this.modalProvider.showModal$({
      component: ActionModalComponent,
      cssClass: 'present-modal',
      componentProps: {
        header: 'Przypisz gierki do grupki',
        message: '',
        btnOk: 'Przypisz',
        btnCancel: 'Anuluj',
        template: AssignGamesToTeamComponent,
        templateInputs: { games },
        requireTemplateSelection: true,
        action: (gameIds?: unknown) => this.assignGames(gameIds as string[])
      }
    });
  }

  private assignGames(gameIds: string[]): void {
    const data: TeamInviteData | undefined = this.teamData();
    if (!data || !gameIds?.length) return;

    this.assignGamesToTeamCommandHandler
      .assign({ teamId: data.teamId, gameIds })
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((result: AssignGamesToTeamCommandResult) =>
        this.toastProvider.showToast({ message: this.buildAssignedMessage(result.assignedCount) })
      );
  }

  private buildAssignedMessage(count: number): string {
    return count === 1 ? 'Przypisano gierkę do grupki' : `Przypisano gierki do grupki (${count})`;
  }
}
